/**
 * Lightweight multilingual text processing utilities
 * designed to work in browser environments without Node.js dependencies
 */

// Language detection utilities
export function detectLanguage(text: string): string {
  // Vietnamese uses additional Unicode characters
  const vietnameseChars = /[àáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/i;
  
  // Japanese uses specific Unicode ranges
  const japaneseChars = /[\u3040-\u30ff\u3400-\u4dbf\u4e00-\u9fff]/;
  
  // Chinese characters (simplified and traditional)
  const chineseChars = /[\u4e00-\u9fff\u3400-\u4dbf]/;
  
  // Korean Hangul
  const koreanChars = /[\uac00-\ud7af\u1100-\u11ff]/;
  
  // Thai script
  const thaiChars = /[\u0e00-\u0e7f]/;
  
  if (vietnameseChars.test(text)) return 'vi';
  if (japaneseChars.test(text)) return 'ja';
  if (chineseChars.test(text)) return 'zh';
  if (koreanChars.test(text)) return 'ko';
  if (thaiChars.test(text)) return 'th';
  
  // Default to English if no specific patterns detected
  return 'en';
}

// Normalize Unicode text
export function normalizeText(text: string): string {
  return text
    .normalize('NFC') // Normalize to composed form (important for Vietnamese)
    .replace(/\s+/g, ' ')
    .trim();
}

// Language-specific stop words
const stopWordsByLanguage: Record<string, string[]> = {
  en: ['the', 'a', 'an', 'and', 'in', 'on', 'at', 'to', 'for', 'with', 'by', 'about', 
      'as', 'is', 'are', 'was', 'were', 'be', 'been', 'being', 'have', 'has', 'had',
      'i', 'you', 'he', 'she', 'it', 'we', 'they', 'this', 'that', 'these', 'those'],
  vi: ['và', 'của', 'có', 'không', 'là', 'được', 'trong', 'đó', 'với', 'cho', 'một', 'như', 
       'để', 'từ', 'khi', 'sẽ', 'tôi', 'bạn', 'anh', 'chị', 'họ', 'chúng', 'những', 'các', 'thì'],
  zh: ['的', '了', '和', '是', '在', '我', '有', '就', '不', '人', '都', '一', '一个', '上', '也', '很', '到', '说', '要', '去', '你', '会', '着', '没有'],
  ja: ['の', 'に', 'は', 'を', 'た', 'が', 'で', 'て', 'と', 'し', 'れ', 'さ', 'ある', 'いる', 'も', 'する', 'から', 'な', 'こと', 'として', 'い', 'や', 'れる'],
  ko: ['이', '가', '은', '는', '을', '를', '의', '에', '에서', '로', '으로', '와', '과', '하고', '이다', '있다', '하다', '되다', '그', '그리고', '또', '또는'],
  th: ['และ', 'ของ', 'มี', 'ไม่', 'ใน', 'ที่', 'เป็น', 'การ', 'ให้', 'ได้', 'จะ', 'แต่', 'หรือ', 'ก็', 'เมื่อ', 'ต้อง', 'เพราะ', 'มาก', 'ความ', 'อยู่']
};

// Tokenize text according to language rules
export function tokenizeByLanguage(text: string, language: string): string[] {
  text = normalizeText(text);
  
  // For languages with no spaces between words, use different strategies
  if (language === 'zh' || language === 'ja' || language === 'th') {
    // For these languages, use character-based tokenization
    const tokens: string[] = [];
    for (let i = 0; i < text.length - 1; i++) {
      if (text[i].trim()) {
        tokens.push(text.substring(i, i + 2));
      }
    }
    return tokens;
  }
  
  // For Vietnamese, we can use word boundaries but need to keep diacritics
  if (language === 'vi') {
    // Use a regex that preserves Vietnamese diacritics
    return text
           .toLowerCase()
           .split(/\s+/)
           .map(word => word.replace(/[.,!?;:()[\]{}'"]/g, ''))
           .filter(word => word.length > 1);
  }
  
  // For other languages like English, use standard tokenization
  return text
         .toLowerCase()
         .split(/\s+/)
         .map(word => word.replace(/[^\w\sàáạảãâầấậẩẫăằắặẳẵèéẹẻẽêềếệểễìíịỉĩòóọỏõôồốộổỗơờớợởỡùúụủũưừứựửữỳýỵỷỹđ]/g, ''))
         .filter(word => word.length > 1);
}

/**
 * Enhanced function to extract keywords from text with multilingual support
 */
export function extractKeywords(text: string): string[] {
  if (!text || text.trim().length === 0) {
    return [];
  }
  
  // Detect language for appropriate processing
  const language = detectLanguage(text);
  
  // Get stop words for the detected language (default to English if not found)
  const stopWords = stopWordsByLanguage[language] || stopWordsByLanguage.en;
  
  // Use language-specific tokenization
  const words = tokenizeByLanguage(text, language);
  
  // Filter out stop words and very short tokens
  const filteredWords = words.filter(word => 
    word.length > (language === 'en' ? 3 : 1) && // Different length requirements for different languages
    !stopWords.includes(word)
  );
  
  // Count word frequency
  const wordCounts: Record<string, number> = {};
  filteredWords.forEach(word => {
    wordCounts[word] = (wordCounts[word] || 0) + 1;
  });
  
  // For English, we can do some simple stemming
  if (language === 'en') {
    // Group similar words (basic stemming)
    const stemmedCounts: Record<string, { count: number; originalWords: string[] }> = {};
    
    Object.entries(wordCounts).forEach(([word, count]) => {
      // Very basic stemming for English (just remove common endings)
      let stem = word;
      if (word.endsWith('ing')) stem = word.slice(0, -3);
      else if (word.endsWith('ed')) stem = word.slice(0, -2);
      else if (word.endsWith('s')) stem = word.slice(0, -1);
      
      if (!stemmedCounts[stem]) {
        stemmedCounts[stem] = {
          count: 0,
          originalWords: []
        };
      }
      
      stemmedCounts[stem].count += count;
      stemmedCounts[stem].originalWords.push(word);
    });
    
    // Select the most common original word for each stem
    const processedCounts: Record<string, number> = {};
    
    Object.entries(stemmedCounts).forEach(([stem, data]) => {
      const bestWord = data.originalWords.reduce((best, current) => 
        (wordCounts[current] > wordCounts[best]) ? current : best
      );
      
      processedCounts[bestWord] = data.count;
    });
    
    return Object.entries(processedCounts)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)  // Return more keywords to improve topic coverage
      .map(([word]) => word);
  }
  
  // For other languages, just use the word counts
  return Object.entries(wordCounts)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 8)  // Return more keywords for non-English languages
    .map(([word]) => word);
}

/**
 * Clusters similar feedback messages together
 * Enhanced with multilingual support
 * @param feedbackItems Array of feedback content strings
 * @returns Object with cluster names as keys and arrays of feedback items as values
 */
export function clusterFeedback(feedbackItems: string[]): { [key: string]: string[] } {
  if (!feedbackItems || feedbackItems.length === 0) {
    return {};
  }

  // First, try to detect the predominant language
  const languageCounts: Record<string, number> = { en: 0, vi: 0, zh: 0, ja: 0, ko: 0, th: 0 };
  
  feedbackItems.forEach(item => {
    const lang = detectLanguage(item);
    languageCounts[lang] = (languageCounts[lang] || 0) + 1;
  });
  
  // Find the most common language
  let predominantLanguage = 'en';
  let maxCount = 0;
  
  Object.entries(languageCounts).forEach(([lang, count]) => {
    if (count > maxCount) {
      maxCount = count;
      predominantLanguage = lang;
    }
  });
  
  // Define topics for the detected language
  const topics: Record<string, Record<string, string[]>> = {
    en: {
      'Incorrect Information': ['wrong', 'incorrect', 'false', 'inaccurate', 'error'],
      'Clarity Issues': ['confusing', 'unclear', 'hard to understand', 'difficult', 'ambiguous'],
      'Missing Context': ['missing', 'incomplete', 'more detail', 'lacking', 'context'],
      'Formatting Problems': ['formatting', 'layout', 'display', 'structure', 'format'],
      'Not Relevant': ['irrelevant', 'off-topic', 'unrelated', 'not what', 'didn\'t address'],
      'Technical Issues': ['bug', 'error', 'crash', 'freeze', 'technical'],
      'Response Too Long': ['too long', 'verbose', 'wordy', 'rambling'],
      'Response Too Short': ['too short', 'brief', 'not enough', 'more information']
    },
    vi: {
      'Thông tin không chính xác': ['sai', 'không đúng', 'không chính xác', 'nhầm', 'lỗi'],
      'Vấn đề rõ ràng': ['khó hiểu', 'không rõ ràng', 'khó hiểu', 'phức tạp', 'mơ hồ'],
      'Thiếu ngữ cảnh': ['thiếu', 'không đầy đủ', 'chi tiết hơn', 'thiếu sót', 'bối cảnh'],
      'Vấn đề định dạng': ['định dạng', 'bố cục', 'hiển thị', 'cấu trúc'],
      'Không liên quan': ['không liên quan', 'ngoài chủ đề', 'không phù hợp', 'không phải'],
      'Vấn đề kỹ thuật': ['lỗi kỹ thuật', 'trục trặc', 'đóng băng', 'kỹ thuật'],
      'Phản hồi quá dài': ['quá dài', 'dài dòng', 'dài', 'lan man'],
      'Phản hồi quá ngắn': ['quá ngắn', 'ngắn gọn', 'không đủ', 'thêm thông tin']
    }
    // Add more languages as needed
  };
  
  // Use English topics as fallback
  const currentTopics = topics[predominantLanguage] || topics.en;

  // Initialize clusters
  const clusters: { [key: string]: string[] } = {};
  Object.keys(currentTopics).forEach(topic => {
    clusters[topic] = [];
  });
  clusters['Other Issues'] = []; // For unclassified feedback

  // Assign feedback to clusters
  feedbackItems.forEach(feedback => {
    const normalizedFeedback = normalizeText(feedback.toLowerCase());
    let assigned = false;

    for (const [topic, keywords] of Object.entries(currentTopics)) {
      if (keywords.some(keyword => normalizedFeedback.includes(keyword))) {
        clusters[topic].push(feedback);
        assigned = true;
        break;
      }
    }

    if (!assigned) {
      clusters['Other Issues'].push(feedback);
    }
  });

  // Remove empty clusters
  const nonEmptyClusters: { [key: string]: string[] } = {};
  Object.entries(clusters).forEach(([topic, items]) => {
    if (items.length > 0) {
      nonEmptyClusters[topic] = items;
    }
  });

  return nonEmptyClusters;
}

interface ResponseFeedbackItem {
  content: string;
  feedback: 'positive' | 'negative';
}

interface CorrelationResult {
  pattern: string;
  count: number;
  positiveCount: number;
  negativeCount: number;
  positiveRate: number;
}

/**
 * Analyzes the correlation between response patterns and feedback
 * Enhanced with multilingual pattern detection
 * @param data Array of response-feedback pairs
 * @returns Array of pattern correlations sorted by positive feedback rate
 */
export function analyzeResponseFeedbackCorrelation(data: ResponseFeedbackItem[]): CorrelationResult[] {
  if (!data || data.length < 5) {
    return [];
  }

  // Detect the predominant language in the dataset
  let viCount = 0, zhCount = 0, jaCount = 0, koCount = 0, thCount = 0;
  
  data.forEach(item => {
    const lang = detectLanguage(item.content);
    if (lang === 'vi') viCount++;
    else if (lang === 'zh') zhCount++;
    else if (lang === 'ja') jaCount++;
    else if (lang === 'ko') koCount++;
    else if (lang === 'th') thCount++;
  });
  
  let predominantLanguage = 'en';
  let maxCount = 0;
  
  const langCounts = [
    { lang: 'en', count: data.length - viCount - zhCount - jaCount - koCount - thCount },
    { lang: 'vi', count: viCount },
    { lang: 'zh', count: zhCount },
    { lang: 'ja', count: jaCount },
    { lang: 'ko', count: koCount },
    { lang: 'th', count: thCount }
  ];
  
  langCounts.forEach(item => {
    if (item.count > maxCount) {
      maxCount = item.count;
      predominantLanguage = item.lang;
    }
  });

  // Define language-specific patterns
  const patternsByLanguage: Record<string, Array<{ name: string, test: (content: string) => boolean }>> = {
    en: [
      {
        name: 'Uses numbered lists',
        test: (content: string) => /\d+\.\s/.test(content),
      },
      {
        name: 'Uses bullet points',
        test: (content: string) => /[•\-\*]\s/.test(content),
      },
      {
        name: 'Includes code examples',
        test: (content: string) => /```/.test(content),
      },
      {
        name: 'Short responses (<300 chars)',
        test: (content: string) => content.length < 300,
      },
      {
        name: 'Medium length (300-800 chars)',
        test: (content: string) => content.length >= 300 && content.length <= 800,
      },
      {
        name: 'Long responses (>800 chars)',
        test: (content: string) => content.length > 800,
      },
      {
        name: 'Asks follow-up questions',
        test: (content: string) => /\?/.test(content),
      },
      {
        name: 'Contains definitions',
        test: (content: string) => /is defined as|refers to|means|is a|are|means that/.test(content.toLowerCase()),
      },
      {
        name: 'Step-by-step instructions',
        test: (content: string) => /first|second|third|step|finally|lastly|then/i.test(content),
      },
      {
        name: 'Uses analogies or examples',
        test: (content: string) => /for example|such as|like|instance|analogy|similar to/i.test(content),
      }
    ],
    vi: [
      {
        name: 'Sử dụng danh sách đánh số',
        test: (content: string) => /\d+\.\s/.test(content),
      },
      {
        name: 'Sử dụng dấu đầu dòng',
        test: (content: string) => /[•\-\*]\s/.test(content),
      },
      {
        name: 'Bao gồm ví dụ mã',
        test: (content: string) => /```/.test(content),
      },
      {
        name: 'Câu trả lời ngắn (<300 ký tự)',
        test: (content: string) => content.length < 300,
      },
      {
        name: 'Độ dài trung bình (300-800 ký tự)',
        test: (content: string) => content.length >= 300 && content.length <= 800,
      },
      {
        name: 'Câu trả lời dài (>800 ký tự)',
        test: (content: string) => content.length > 800,
      },
      {
        name: 'Đặt câu hỏi tiếp theo',
        test: (content: string) => /\?/.test(content),
      },
      {
        name: 'Chứa định nghĩa',
        test: (content: string) => /được định nghĩa là|là|có nghĩa là|được hiểu là|là một|là những|có nghĩa rằng/i.test(content),
      },
      {
        name: 'Hướng dẫn từng bước',
        test: (content: string) => /đầu tiên|thứ hai|thứ ba|bước|cuối cùng|sau đó/i.test(content),
      },
      {
        name: 'Sử dụng ví dụ hoặc so sánh',
        test: (content: string) => /ví dụ|chẳng hạn|như|trường hợp|tương tự như/i.test(content),
      }
    ]
    // Add more languages as needed
  };

  // Use English patterns as default
  const patternTests = patternsByLanguage[predominantLanguage] || patternsByLanguage.en;
  const results: CorrelationResult[] = [];

  // Analyze each pattern
  patternTests.forEach(patternTest => {
    let matchCount = 0;
    let positiveMatchCount = 0;

    data.forEach(item => {
      if (patternTest.test(item.content)) {
        matchCount++;
        if (item.feedback === 'positive') {
          positiveMatchCount++;
        }
      }
    });

    if (matchCount >= 3) { // Only include patterns with enough samples
      results.push({
        pattern: patternTest.name,
        count: matchCount,
        positiveCount: positiveMatchCount,
        negativeCount: matchCount - positiveMatchCount,
        positiveRate: (positiveMatchCount / matchCount) * 100
      });
    }
  });

  // Sort by positive feedback rate
  return results.sort((a, b) => b.positiveRate - a.positiveRate);
}
